import {
  entryObservabilityRequestPath,
  PROFIT_V1_DASHBOARD_FALLBACK,
  resolveCycleProgressTarget,
} from "./tradeCycle";
import type {
  AlertCandidatesResponse,
  AlertIngestHealthResponse,
  DataHealthResponse,
  DataHealthSnapshot,
  EventTruthTapeStatusResponse,
  CollaborationNotesResponse,
  DashboardData,
  CompanyEventsResponse,
  ExecutionDelegateResponse,
  CycleOperatorSummary,
  EntryObservabilityResponse,
  HealthResponse,
  LedgerSummaryResponse,
  PortfolioResponse,
  ProposalsResponse,
  RuntimeStatusResponse,
  SourceReliabilityResponse,
  SubagentsResponse,
  TeamMessagesResponse,
} from "./types";
import { captureDashboardEvent } from "./analytics";
import { captureDashboardError } from "./errorTracking";

const configuredBase = import.meta.env.VITE_HYPERMAX_API_BASE_URL?.replace(/\/+$/, "");
const retryableStatuses = new Set([408, 425, 429, 500, 502, 503, 504]);
const defaultReadTimeoutMs = 5000;
const backgroundReadTimeouts: Array<[RegExp, number]> = [
  [/^\/entry\/observability/, 15_000],
  [/^\/cycle\/operator-summary/, 8_000],
  [/^\/ledger\/summary/, 8_000],
  [/^\/sources\/reliability/, 15_000],
  [/^\/research\/event-truth-tape\/status/, 15_000],
  [/^\/observability\/data-health/, 15_000],
];

/** Deferred panels — failures must not trip the global API circuit breaker. */
function isDeferredDashboardPath(path: string) {
  return (
    path.startsWith("/sources/reliability") ||
    path.startsWith("/company/events") ||
    path.startsWith("/team/messages") ||
    path.startsWith("/proposals") ||
    path.startsWith("/research/event-truth-tape/status") ||
    path.startsWith("/observability/data-health")
  );
}

let lastEntryObservability: EntryObservabilityResponse | undefined;
let lastCycleOperatorSummary: CycleOperatorSummary | undefined;
let lastLedgerSummary: LedgerSummaryResponse | undefined;

function isEntryObservabilityPath(path: string) {
  return path.startsWith("/entry/observability");
}

/** Operator-critical reads — must not be blocked by dashboard circuit breaker. */
function isOperatorCriticalPath(path: string) {
  return (
    isEntryObservabilityPath(path) ||
    path.startsWith("/cycle/operator-summary") ||
    path.startsWith("/ledger/summary")
  );
}

function shouldCountTowardCircuitBreaker(path: string) {
  return !isDeferredDashboardPath(path) && !isOperatorCriticalPath(path);
}

function isShellCriticalSuccessPath(path: string) {
  return path === "/health" || path === "/portfolio";
}

type CircuitState = "closed" | "open" | "half_open";

class DashboardApiCircuitBreaker {
  #failures = 0;
  #openedAt = 0;

  constructor(
    private readonly failureThreshold = 4,
    private readonly resetAfterMs = 30_000,
  ) {}

  get state(): CircuitState {
    if (this.#openedAt === 0) return "closed";
    return performance.now() - this.#openedAt >= this.resetAfterMs ? "half_open" : "open";
  }

  canAttempt() {
    return this.state !== "open";
  }

  recordSuccess() {
    this.#failures = 0;
    this.#openedAt = 0;
  }

  recordFailure() {
    this.#failures += 1;
    if (this.#failures >= this.failureThreshold) {
      this.#openedAt = performance.now();
    }
  }

  reset() {
    this.#failures = 0;
    this.#openedAt = 0;
  }
}

const apiCircuitBreaker = new DashboardApiCircuitBreaker();

export function normalizeDataHealthResponse(raw: unknown): DataHealthResponse {
  const fetchObservedAt = new Date().toISOString();
  const payload = raw as DataHealthResponse & DataHealthSnapshot;
  if (
    payload &&
    typeof payload === "object" &&
    payload.schemaVersion === "data-health-v1" &&
    !("snapshot" in payload)
  ) {
    const snapshot = payload as DataHealthSnapshot;
    return {
      observerOnly: true,
      observedAt: fetchObservedAt,
      available: true,
      snapshot,
      note: "observer_only_no_live_promotion",
    };
  }
  const wrapped = payload as DataHealthResponse;
  if (wrapped && typeof wrapped === "object" && wrapped.available && wrapped.snapshot) {
    return { ...wrapped, observedAt: wrapped.observedAt ?? fetchObservedAt };
  }
  return wrapped;
}

function apiUrl(path: string) {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  if (configuredBase) return `${configuredBase}${cleanPath}`;
  return `/api${cleanPath}`;
}

function createTraceHeaders() {
  const traceId = crypto.randomUUID().replace(/-/g, "");
  const requestId = crypto.randomUUID();
  const spanId = crypto.randomUUID().replace(/-/g, "").slice(0, 16);
  return {
    requestId,
    traceId,
    traceparent: `00-${traceId}-${spanId}-01`,
  };
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function dashboardApiTimeoutMs(path: string) {
  return (
    backgroundReadTimeouts.find(([pattern]) => pattern.test(path))?.[1] ?? defaultReadTimeoutMs
  );
}

function isRetryableResponse(response: Response) {
  return retryableStatuses.has(response.status);
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError";
}

async function readJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const startedAt = performance.now();
  const trace = createTraceHeaders();
  const maxAttempts = 2;

  if (!apiCircuitBreaker.canAttempt() && !isOperatorCriticalPath(path)) {
    const error = Object.assign(new Error("dashboard_api_circuit_open"), {
      path,
      requestId: trace.requestId,
      traceId: trace.traceId,
      circuitState: apiCircuitBreaker.state,
    });
    captureDashboardEvent("dashboard_api_request", {
      method: "GET",
      path,
      durationMs: Math.round(performance.now() - startedAt),
      ok: false,
      attempt: 0,
      breakerState: apiCircuitBreaker.state,
      retryable: false,
    });
    captureDashboardError(error, {
      action: "dashboard_api_circuit_open",
      path,
      requestId: trace.requestId,
      traceId: trace.traceId,
      circuitState: apiCircuitBreaker.state,
    });
    throw error;
  }

  // Per-request timeout: avoid a single hung call stalling the entire refresh cycle
  const perReqSignal = AbortSignal.timeout(dashboardApiTimeoutMs(path));
  const combined = signal ? AbortSignal.any([signal, perReqSignal]) : perReqSignal;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const response = await fetch(apiUrl(path), {
        signal: combined,
        headers: {
          accept: "application/json",
          "x-request-id": trace.requestId,
          "x-hypermax-trace-id": trace.traceId,
          traceparent: trace.traceparent,
        },
      });
      const retryable = isRetryableResponse(response);
      const durationMs = Math.round(performance.now() - startedAt);
      captureDashboardEvent("dashboard_api_request", {
        method: "GET",
        path,
        durationMs,
        statusCode: response.status,
        ok: response.ok,
        attempt,
        breakerState: apiCircuitBreaker.state,
        retryable,
      });
      if (response.ok) {
        if (isShellCriticalSuccessPath(path)) {
          apiCircuitBreaker.recordSuccess();
        }
        return response.json() as Promise<T>;
      }
      if (retryable && attempt < maxAttempts) {
        await sleep(25 * attempt);
        continue;
      }

      const body = await response.text();
      const error = Object.assign(new Error(`${response.status} ${body.slice(0, 180)}`), {
        path,
        requestId: response.headers.get("x-request-id") ?? trace.requestId,
        traceId: response.headers.get("x-hypermax-trace-id") ?? trace.traceId,
        statusCode: response.status,
      });
      if (shouldCountTowardCircuitBreaker(path)) {
        apiCircuitBreaker.recordFailure();
      }
      captureDashboardError(error, {
        action: "dashboard_api_read",
        path,
        requestId: error.requestId,
        traceId: error.traceId,
        statusCode: response.status,
        attempt,
        circuitState: apiCircuitBreaker.state,
      });
      throw error;
    } catch (error) {
      if (isAbortError(error)) throw error;
      if (typeof error === "object" && error !== null && "statusCode" in error) {
        throw error;
      }
      if (attempt < maxAttempts) {
        await sleep(25 * attempt);
        continue;
      }
      if (shouldCountTowardCircuitBreaker(path)) {
        apiCircuitBreaker.recordFailure();
      }
      captureDashboardError(error, {
        action: "dashboard_api_read",
        path,
        requestId: trace.requestId,
        traceId: trace.traceId,
        attempt,
        circuitState: apiCircuitBreaker.state,
      });
      throw error;
    }
  }
  throw new Error("dashboard_api_retry_exhausted");
}

async function postJson<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const startedAt = performance.now();
  const trace = createTraceHeaders();
  const response = await fetch(apiUrl(path), {
    method: "POST",
    signal,
    headers: {
      accept: "application/json",
      "content-type": "application/json",
      "x-request-id": trace.requestId,
      "x-hypermax-trace-id": trace.traceId,
      traceparent: trace.traceparent,
    },
    body: JSON.stringify(body),
  });
  const durationMs = Math.round(performance.now() - startedAt);
  captureDashboardEvent("dashboard_api_request", {
    method: "POST",
    path,
    durationMs,
    statusCode: response.status,
    ok: response.ok,
  });
  if (!response.ok) {
    const text = await response.text();
    const error = Object.assign(new Error(`${response.status} ${text.slice(0, 180)}`), {
      path,
      requestId: response.headers.get("x-request-id") ?? trace.requestId,
      traceId: response.headers.get("x-hypermax-trace-id") ?? trace.traceId,
      statusCode: response.status,
    });
    captureDashboardError(error, {
      action: "dashboard_api_write",
      path,
      requestId: error.requestId,
      traceId: error.traceId,
      statusCode: response.status,
    });
    throw error;
  }
  return response.json() as Promise<T>;
}

export async function fetchCycleOperatorSummary(signal?: AbortSignal) {
  try {
    const data = await readJson<CycleOperatorSummary>("/cycle/operator-summary", signal);
    lastCycleOperatorSummary = data;
    return data;
  } catch (error) {
    if (lastCycleOperatorSummary) return lastCycleOperatorSummary;
    throw error;
  }
}

export async function fetchLedgerSummary(signal?: AbortSignal) {
  try {
    const data = await readJson<LedgerSummaryResponse>("/ledger/summary?limit=80", signal);
    lastLedgerSummary = data;
    return data;
  } catch (error) {
    if (lastLedgerSummary) return lastLedgerSummary;
    throw error;
  }
}

/** In-memory ledger from the last successful fetch (SWR trade tape on cold start). */
export function peekLedgerSummary(): LedgerSummaryResponse | undefined {
  return lastLedgerSummary;
}

/** Explicit null → profit-v1 fallback for callers that opt in; hook cold-start uses undefined + gateUnknown. */
export function cycleSummaryForBadge(
  cycle: CycleOperatorSummary | null | undefined,
): CycleOperatorSummary | null | undefined {
  if (cycle === undefined) return undefined;
  if (cycle === null) return PROFIT_V1_DASHBOARD_FALLBACK;
  return cycle;
}

export async function fetchEntryObservability(
  cycle?: CycleOperatorSummary | null,
  signal?: AbortSignal,
) {
  const cycleForPath = cycle ?? lastCycleOperatorSummary;
  const requiredTrades = resolveCycleProgressTarget(cycleForPath).requiredTrades;
  const prevRequired = lastEntryObservability?.naturalTradeProgress?.requiredTrades;
  if (prevRequired != null && requiredTrades > 0 && prevRequired !== requiredTrades) {
    lastEntryObservability = undefined;
  }
  const path = entryObservabilityRequestPath(cycleForPath);
  try {
    const data = await readJson<EntryObservabilityResponse>(path, signal);
    lastEntryObservability = data;
    return data;
  } catch (error) {
    if (lastEntryObservability) return lastEntryObservability;
    throw error;
  }
}

export function __resetDashboardApiResilienceForTests() {
  apiCircuitBreaker.reset();
  lastEntryObservability = undefined;
  lastCycleOperatorSummary = undefined;
  lastLedgerSummary = undefined;
}

export const __dashboardApiTimeoutMsForTests = dashboardApiTimeoutMs;

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "연결 상태를 확인하고 있습니다.";
}

type CollaborationNote = CollaborationNotesResponse["notes"][number];

function terminalCollabTime(note: CollaborationNote) {
  return note.closedAt ?? note.verdict?.decidedAt ?? note.createdAt;
}

function isNonRuntimeEvidenceTrigger(trigger: unknown) {
  if (typeof trigger !== "string") return false;
  const normalized = trigger.toLowerCase();
  return (
    normalized.startsWith("smoke") ||
    normalized.includes(":smoke") ||
    normalized.startsWith("price_wake") ||
    normalized.startsWith("price-wake")
  );
}

function isRuntimeEvidenceCollaborationNote(note: CollaborationNote) {
  return !(note.triggers ?? []).some(isNonRuntimeEvidenceTrigger);
}

function normalizeCatalystCalibration(value: unknown): unknown[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value;
  if (
    typeof value === "object" &&
    value !== null &&
    "catalystType" in value &&
    typeof (value as { catalystType?: unknown }).catalystType === "string"
  ) {
    return [value];
  }
  return [];
}

function collaborationLearningCounts(notes: CollaborationNote[]) {
  const closed = notes.filter((note) => note.status === "closed");
  return {
    closedAwaitingCeoReviewCount: closed.filter((note) => (note.ceoReview ?? []).length === 0)
      .length,
    closedReviewedCount: closed.filter((note) => (note.ceoReview ?? []).length > 0).length,
    learningSignalCount: closed.reduce((sum, note) => {
      return (
        sum +
        (note.ceoReview ?? []).filter(
          (review) =>
            (review.sourceUpdates ?? []).length ||
            normalizeCatalystCalibration(review.catalystCalibration).length,
        ).length
      );
    }, 0),
  };
}

const DASHBOARD_COLLAB_STATUSES = new Set(["decided", "closed"]);

function mergeCollaborationNotes(
  ...responses: Array<CollaborationNotesResponse | undefined>
): CollaborationNotesResponse {
  const byId = new Map<string, CollaborationNote>();
  for (const response of responses) {
    for (const note of response?.notes ?? []) {
      if (!DASHBOARD_COLLAB_STATUSES.has(note.status)) continue;
      byId.set(note.id, note);
    }
  }
  const notes = [...byId.values()]
    .filter(isRuntimeEvidenceCollaborationNote)
    .sort((left, right) => terminalCollabTime(right).localeCompare(terminalCollabTime(left)))
    .slice(0, 8);
  return {
    notes,
    ...collaborationLearningCounts(notes),
  };
}

type DashboardFetchCapture = ReturnType<typeof createDashboardFetchCapture>;

export function createDashboardFetchCapture() {
  const criticalErrors: Record<string, string> = {};
  const backgroundErrors: Record<string, string> = {};

  function makeCapture(target: Record<string, string>) {
    return async function capture<T>(
      key: string,
      task: Promise<T>,
      fallback?: T,
    ): Promise<T | undefined> {
      try {
        return await task;
      } catch (error) {
        target[key] = errorMessage(error);
        return fallback;
      }
    };
  }

  return {
    criticalErrors,
    backgroundErrors,
    captureCritical: makeCapture(criticalErrors),
    captureBackground: makeCapture(backgroundErrors),
    finalizeErrors(): DashboardData["errors"] | undefined {
      const hasCritical = Object.keys(criticalErrors).length > 0;
      const hasBackground = Object.keys(backgroundErrors).length > 0;
      if (!hasCritical && !hasBackground) return undefined;
      return {
        critical: hasCritical ? { ...criticalErrors } : undefined,
        background: hasBackground ? { ...backgroundErrors } : undefined,
      };
    },
  };
}

/** First paint shell: health, portfolio, org — ledger loads separately (SWR). */
export async function fetchDashboardCriticalShell(
  signal?: AbortSignal,
  capture: DashboardFetchCapture = createDashboardFetchCapture(),
): Promise<
  Pick<DashboardData, "health" | "portfolio" | "subagents" | "executionDelegate" | "errors">
> {
  const [health, portfolio, subagents, executionDelegate] = await Promise.all([
    capture.captureCritical("health", readJson<HealthResponse>("/health", signal)),
    capture.captureCritical("portfolio", readJson<PortfolioResponse>("/portfolio", signal)),
    capture.captureCritical(
      "subagents",
      readJson<SubagentsResponse>("/subagents?reportLimit=1&redirectionLimit=1", signal),
      { subagents: [] },
    ),
    capture.captureCritical(
      "executionDelegate",
      readJson<ExecutionDelegateResponse>("/execution-delegate", signal),
      { executionDelegate: null },
    ),
  ]);

  return {
    health,
    portfolio,
    subagents,
    executionDelegate,
    errors: capture.finalizeErrors(),
  };
}

/** First paint: health, portfolio, org shell, ledger (positions tab). */
export async function fetchDashboardCriticalData(
  signal?: AbortSignal,
  capture: DashboardFetchCapture = createDashboardFetchCapture(),
): Promise<
  Pick<
    DashboardData,
    "health" | "portfolio" | "subagents" | "executionDelegate" | "ledger" | "errors"
  >
> {
  const [shell, ledger] = await Promise.all([
    fetchDashboardCriticalShell(signal, capture),
    capture.captureCritical("ledger", fetchLedgerSummary(signal)),
  ]);

  return {
    ...shell,
    ledger,
    errors: capture.finalizeErrors(),
  };
}

/** Overview-critical background panels — smaller payloads, single collab fetch. */
export async function fetchDashboardPriorityBackground(
  signal?: AbortSignal,
  capture: DashboardFetchCapture = createDashboardFetchCapture(),
): Promise<
  Pick<
    DashboardData,
    "alertCandidates" | "alertIngestHealth" | "runtimeStatus" | "collaborationNotes" | "errors"
  >
> {
  const [
    alertCandidates,
    alertIngestHealth,
    runtimeStatus,
    decidedCollaborationNotes,
    closedCollaborationNotes,
  ] = await Promise.all([
    capture.captureBackground(
      "alertCandidates",
      readJson<AlertCandidatesResponse>(
        "/alerts/candidates?limit=24&includeStale=true&minScore=0",
        signal,
      ),
      { alerts: [] },
    ),
    capture.captureBackground(
      "alertIngestHealth",
      readJson<AlertIngestHealthResponse>("/alerts/ingest-health", signal),
      {
        okCount: 0,
        failingCount: 0,
        cooldownCount: 0,
        disabledCount: 0,
        reviewSources: [],
        sources: [],
      },
    ),
    capture.captureBackground(
      "runtimeStatus",
      readJson<RuntimeStatusResponse>("/runtime/status", signal),
      {},
    ),
    capture.captureBackground(
      "decidedCollaborationNotes",
      readJson<CollaborationNotesResponse>("/collaboration-notes?status=decided&limit=12", signal),
      { notes: [] },
    ),
    capture.captureBackground(
      "closedCollaborationNotes",
      readJson<CollaborationNotesResponse>("/collaboration-notes?status=closed&limit=12", signal),
      { notes: [] },
    ),
  ]);

  return {
    alertCandidates,
    alertIngestHealth,
    runtimeStatus,
    collaborationNotes: mergeCollaborationNotes(
      decidedCollaborationNotes,
      closedCollaborationNotes,
    ),
    errors: capture.finalizeErrors(),
  };
}

/** Lower-priority panels (activity/org tabs) — loaded after first paint. */
export async function fetchDashboardDeferredBackground(
  signal?: AbortSignal,
  capture: DashboardFetchCapture = createDashboardFetchCapture(),
): Promise<
  Pick<
    DashboardData,
    | "companyEvents"
    | "teamMessages"
    | "proposals"
    | "sources"
    | "eventTruthTape"
    | "dataHealth"
    | "errors"
  >
> {
  const [companyEvents, teamMessages, proposals, sources, eventTruthTape, dataHealth] =
    await Promise.all([
      capture.captureBackground(
        "companyEvents",
        readJson<CompanyEventsResponse>("/company/events?limit=20", signal),
        { events: [] },
      ),
      capture.captureBackground(
        "teamMessages",
        readJson<TeamMessagesResponse>("/team/messages?limit=12", signal),
        { messages: [] },
      ),
      capture.captureBackground(
        "proposals",
        readJson<ProposalsResponse>("/proposals?limit=10", signal),
        {
          proposals: [],
        },
      ),
      capture.captureBackground(
        "sources",
        readJson<SourceReliabilityResponse>("/sources/reliability?limit=80&maxSources=12", signal),
        { generatedAt: "", totalSources: 0, sources: [] },
      ),
      capture.captureBackground(
        "eventTruthTape",
        readJson<EventTruthTapeStatusResponse>("/research/event-truth-tape/status", signal),
        {
          available: false,
          observerOnly: true,
          observedAt: "",
          researchDir: "",
          note: "",
        },
      ),
      capture.captureBackground(
        "dataHealth",
        readJson<DataHealthResponse>("/observability/data-health", signal).then(
          normalizeDataHealthResponse,
        ),
        {
          available: false,
          observerOnly: true,
          observedAt: "",
          note: "observer_only_no_live_promotion",
        },
      ),
    ]);

  return {
    companyEvents,
    teamMessages,
    proposals,
    sources,
    eventTruthTape,
    dataHealth,
    errors: capture.finalizeErrors(),
  };
}

/** Secondary panels; does not block positions/overview shell. */
export async function fetchDashboardBackgroundData(
  signal?: AbortSignal,
  capture: DashboardFetchCapture = createDashboardFetchCapture(),
): Promise<
  Pick<
    DashboardData,
    | "companyEvents"
    | "teamMessages"
    | "proposals"
    | "sources"
    | "alertCandidates"
    | "alertIngestHealth"
    | "runtimeStatus"
    | "collaborationNotes"
    | "errors"
  >
> {
  const [priority, deferred] = await Promise.all([
    fetchDashboardPriorityBackground(signal, capture),
    fetchDashboardDeferredBackground(signal, capture),
  ]);

  return {
    ...priority,
    ...deferred,
    errors: mergeDashboardErrors(priority.errors, deferred.errors),
  };
}

export function mergeDashboardErrors(
  critical?: DashboardData["errors"],
  background?: DashboardData["errors"],
): DashboardData["errors"] | undefined {
  const mergedCritical = { ...critical?.critical, ...background?.critical };
  const mergedBackground = { ...critical?.background, ...background?.background };
  const hasCritical = Object.keys(mergedCritical).length > 0;
  const hasBackground = Object.keys(mergedBackground).length > 0;
  if (!hasCritical && !hasBackground) return undefined;
  return {
    critical: hasCritical ? mergedCritical : undefined,
    background: hasBackground ? mergedBackground : undefined,
  };
}

export async function fetchDashboardData(signal?: AbortSignal): Promise<DashboardData> {
  const capture = createDashboardFetchCapture();
  const critical = await fetchDashboardCriticalData(signal, capture);
  const background = await fetchDashboardBackgroundData(signal, capture);

  return {
    ...critical,
    ...background,
    // Entry observability is polled by useTradeCycleProgress (4s) — avoid duplicate heavy fetch here.
    entryObservability: lastEntryObservability,
    errors: mergeDashboardErrors(critical.errors, background.errors),
  };
}

export async function sendDashboardMessage(body: string, signal?: AbortSignal): Promise<unknown> {
  return postJson(
    "/team/messages",
    {
      from: "owner",
      to: "CEO",
      subject: "Owner message",
      body,
      priority: "normal",
      requiresResponse: true,
    },
    signal,
  );
}
